import { resolveRegion, resolveCountryName } from '@/helpers/iso-country-to-region'

const KEY_RE = /^destinations\[(\d+)\]\[(code|arrival|departure|type)\]$/i

const isValidYmd = (value) => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false

    const timestamp = Date.parse(`${value}T00:00:00Z`)
    if (isNaN(timestamp)) return false

    return new Date(timestamp).toISOString().slice(0, 10) === value
}

const firstValue = (value) => (Array.isArray(value) ? value[0] : value)

// The raw code as sent by the partner, trimmed and upper-cased, when it is an
// ISO 3166-1 alpha-2 country code. Transit legs send this code to the recommend
// API as `Transits[].Place` (e.g. "SG"). Region-name codes have none, so this
// returns '' and such a transit is dropped from the payload.
const isoCode = (value) => {
    const normalized = String(value || '').trim().toUpperCase()
    return /^[A-Z]{2}$/.test(normalized) ? normalized : ''
}

// Parse a single leg group into a normalized shape, or null when its `code`
// can't be resolved to a region.
const parseLeg = (group) => {
    const region = resolveRegion(group.code)
    if (!region) return null

    return {
        type: String(group.type || '').trim().toLowerCase(),
        // Region the code maps to — drives the dropdown (only 8 regions exist)
        // and is sent as a destination's `Place`.
        code: region,
        name: region,
        // Raw ISO country code from the URL. Only used for transit legs, which
        // send it as `Transits[].Place`. Empty when a region name was passed.
        place_code: isoCode(group.code),
        arrival_date: isValidYmd(group.arrival) ? group.arrival : '',
        departure_date: isValidYmd(group.departure) ? group.departure : '',
    }
}

// Parse an itinerary deep link into destination rows. Transit legs are NOT their
// own rows: each is nested under the destination it belongs to (the destination
// immediately before it — the convention every deep-link example follows), so a
// transit rides along with its destination's recommend call as a `Transits[]`
// entry rather than getting its own plan. `origin` legs are dropped entirely.
export const parseItineraryQuery = (query) => {
    if (!query || typeof query !== 'object') return []

    const groups = new Map()
    Object.keys(query).forEach(key => {
        const match = key.match(KEY_RE)
        if (!match) return

        const index = Number(match[1])
        const field = match[2].toLowerCase()
        const group = groups.get(index) || {}
        group[field] = firstValue(query[key])
        groups.set(index, group)
    })

    const legs = [...groups.keys()]
        .sort((a, b) => a - b)
        .map(index => parseLeg(groups.get(index)))
        .filter(Boolean)
        // exclude "origin" from destination - business decision
        .filter(leg => leg.type !== 'origin')

    const destinations = []
    // Transits seen before any destination are buffered and attached to the
    // first destination that appears, so a leading layover is never lost.
    let pending_transits = []

    legs.forEach(leg => {
        if (leg.type === 'transit') {
            const transit = {
                // The transit is always a country code; `place_code` (its ISO
                // code) is what the BE wants, and the resolved country name is
                // shown on the badge.
                place_code: leg.place_code,
                name: leg.place_code ? resolveCountryName(leg.place_code) : '',
                arrival_date: leg.arrival_date,
                departure_date: leg.departure_date,
            }

            const current = destinations[destinations.length - 1]
            if (current) {
                current.transits.push(transit)
            } else {
                pending_transits.push(transit)
            }
            return
        }

        // Destination (or any non-origin, non-transit leg): starts a new row and
        // adopts any transits that preceded the first destination. The recommend
        // call sends the region as `Place`, so the destination's raw ISO code is
        // not carried — only its resolved region (`code`).
        destinations.push({
            code: leg.code,
            name: leg.name,
            arrival_date: leg.arrival_date,
            departure_date: leg.departure_date,
            transits: pending_transits,
        })
        pending_transits = []
    })

    return destinations
}

// True when the query contains ANY `destinations[...]` param — i.e. a deep link
// was attempted, even if it yields no valid rows. Lets the page tell an invalid
// deep link (redirect) apart from plain direct navigation (normal empty form).
export const hasItineraryParams = (query) =>
    !!query &&
    typeof query === 'object' &&
    Object.keys(query).some(key => /^destinations\[/i.test(key))

// Validation shared with prefill
export const todayYmd = () => {
    const now = new Date()
    const offset = now.getTimezoneOffset()
    return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 10)
}

export const DESTINATION_FIELDS = ['code', 'arrival_date', 'departure_date']

// check_empty: flag empty fields
export const destinationFieldErrorKey = (destination, field, { today = todayYmd(), check_empty = false } = {}) => {
    const { code, arrival_date, departure_date } = destination || {}

    if (field === 'code') {
        return check_empty && !code ? 'travel_esim.error_destination_required' : ''
    }

    if (field === 'arrival_date') {
        if (!arrival_date) return check_empty ? 'travel_esim.error_arrival_required' : ''
        if (arrival_date < today) return 'travel_esim.error_arrival_past'
        return ''
    }

    if (field === 'departure_date') {
        if (!departure_date) return check_empty ? 'travel_esim.error_departure_required' : ''
        if (departure_date < today) return 'travel_esim.error_departure_past'
        if (arrival_date && departure_date < arrival_date) return 'travel_esim.error_departure_before_arrival'
        return ''
    }

    return ''
}

export const isDestinationValid = (destination, today = todayYmd()) =>
    DESTINATION_FIELDS.every(field => !destinationFieldErrorKey(destination, field, { today, check_empty: true }))

export const areDestinationsValid = (destinations, today = todayYmd()) =>
    Array.isArray(destinations)
    && destinations.length > 0
    && destinations.every(destination => isDestinationValid(destination, today))

export default parseItineraryQuery
